import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { storeReturnsApi } from '../services/api';
import { formatDateTime, formatMoney } from './storeReturnsUtils';

const PAGE_SIZE = 50;

/** Paid store orders the store has not shipped by the promised date. */
const LateShipmentsList = () => {
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await storeReturnsApi.lateShipments({ page, size: PAGE_SIZE });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows(null);
      setError(err.backendMessage || err.message || 'Failed to load late shipments');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading late shipments" />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
        {error}
      </Alert>
    );
  }
  if (!rows || (rows.length === 0 && page === 0)) {
    return <Alert severity="success">No late shipments. Every paid order has shipped on time.</Alert>;
  }

  return (
    <Paper>
      <TableContainer>
        <Table size="small" aria-label="Late shipments">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Buyer</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Promised by</TableCell>
              <TableCell>Flagged late</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.orderId} hover>
                <TableCell>
                  <Link component={RouterLink} to={`/orders/${r.orderId}`}>#{r.orderId}</Link>
                </TableCell>
                <TableCell>{r.storeName || '—'}</TableCell>
                <TableCell>{r.buyerName || '—'}</TableCell>
                <TableCell align="right">{formatMoney(r.total, r.currencyCode)}</TableCell>
                <TableCell>{formatDateTime(r.paidAt)}</TableCell>
                <TableCell>{formatDateTime(r.promisedBy)}</TableCell>
                <TableCell>{formatDateTime(r.lateFlaggedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && (
        <Typography sx={{ p: 2 }} color="text.secondary">No more late shipments.</Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
        <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button size="small" disabled={rows.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </Box>
    </Paper>
  );
};

export default LateShipmentsList;
